import { Modal } from "antd";
import { getOrgChartPublicApi } from "api/services/employee";
import Image from "components/Image";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

const OrgChartPage = () => {
  const { user } = useSelector((state) => state.user);
  const { userData: userList } = useSelector((state) => state.common);
  const [orgChartData, setOrgChartData] = useState(null);
  const [orgChartRoots, setOrgChartRoots] = useState([]); // For new public endpoint - multiple root nodes
  const [orgChartLoading, setOrgChartLoading] = useState(false);
  const [orgDirection, setOrgDirection] = useState("horizontal");
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [zoomLevel, setZoomLevel] = useState(1);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const chartContainerRef = useRef(null);
  const rootNodeRef = useRef(null);
  const zoomContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.includes("/company-directory") ? "/company-directory" : "/employees";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hasOrgChartAccess =
    user?.role?.permissions?.some((p) => p.code === "org_chart") ||
    user?.modules?.some?.((m) => m.code === "org_chart");

  const allowedEmployeeIds = useMemo(() => userList?.map((u) => u.id) || [], [userList]);

  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const levelColors = ["#ffffff", "#f8fafc", "#f1f5f9", "#e0f2fe", "#eef2ff", "#ecfdf3"];

  // Helper function to collect all node IDs recursively
  const getAllNodeIds = (nodes) => {
    const ids = new Set();
    const traverse = (node) => {
      if (!node) return;
      ids.add(node.id);
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => traverse(child));
      }
    };
    nodes.forEach(node => traverse(node));
    return ids;
  };

  // Expand all nodes
  const expandAll = () => {
    if (orgChartRoots.length > 0) {
      const allIds = getAllNodeIds(orgChartRoots);
      setExpandedIds(allIds);
    } else if (orgChartData) {
      const allIds = getAllNodeIds([orgChartData]);
      setExpandedIds(allIds);
    }
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  const OrgChartNode = ({ node, level = 0, direction = orgDirection }) => {
    if (!node) return null;
    const childCount = node.children?.length || 0;
    const isExpanded = expandedIds.has(node.id);
    const cardColor = levelColors[level % levelColors.length];

    const toggleExpand = () => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(node.id)) {
          next.delete(node.id);
        } else {
          next.add(node.id);
        }
        return next;
      });
    };

    const handleProfileImageClick = (e) => {
      e.stopPropagation();
      setSelectedProfile(node);
      setProfileModalOpen(true);
    };

    return (
      <div className="org-node">
        <div className="org-card" style={{ background: cardColor }}>
          <div className="org-avatar" onClick={handleProfileImageClick} style={{ cursor: "pointer" }}>
            {node.profile_image ? (
              <Image
                src={node.profile_image}
                type="user"
                alt={node.name}
                style={{ width: "44px", height: "44px", borderRadius: "50%", objectFit: "cover" }}
              />
            ) : (
              <div className="org-placeholder">{getInitials(node.name)}</div>
            )}
          </div>
          <div className="org-meta">
            {allowedEmployeeIds.includes(node.id) ? (
              <Link
                to={`${basePath}/${node.id}`}
                className="org-name"
                style={{ color: "rgb(211, 0, 26)", textDecoration: "none" }}
              >
                {node.name}
              </Link>
            ) : (
              <div className="org-name" style={{ color: "#111827" }}>
                {node.name}
              </div>
            )}
            {/* {node.emp_code && <div className="org-code">{node.emp_code}</div>} */}
            {node.title && <div className="org-title">{node.title}</div>}
            {node.department && <div className="org-dept">{node.department}</div>}
          </div>
          {childCount > 0 && (
            <button
              type="button"
              className="org-toggle"
              onClick={toggleExpand}
              aria-label={isExpanded ? "Collapse reports" : "Expand reports"}
            >
              <i className={`las ${isExpanded ? "la-minus" : "la-plus"}`} />
              <span className="org-count">{childCount}</span>
            </button>
          )}
        </div>
        {childCount > 0 && isExpanded && (
          <div className={`org-children ${direction === "vertical" ? "vertical" : ""}`}>
            {node.children.map((child) => (
              <OrgChartNode key={child.id} node={child} level={level + 1} direction={direction} />
            ))}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!hasOrgChartAccess) return;

    const companyId =
      user?.employment?.company?.id ??
      user?.company?.id ??
      user?.company_id ??
      user?.employment?.company_user?.company?.id ??
      1; // Default to 1 if not found

    setOrgChartLoading(true);
    setError(null);
    
    // New public endpoint - only needs company parameter
    const params = {
      company: companyId,
    };

    getOrgChartPublicApi(params)
      .then((res) => {
        const results = res.data?.results || [];
        // Filter root nodes (manager_id === null) and set them
        const rootNodes = results.filter((node) => node.manager_id === null);
        setOrgChartRoots(rootNodes);
        // By default, nodes are collapsed (empty Set)
        setExpandedIds(new Set());
      })
      .catch((err) => {
        console.error("Error fetching org chart", err);
        setOrgChartRoots([]);
        setError("Unable to load org chart");
      })
      .finally(() => setOrgChartLoading(false));

    // OLD ENDPOINT CODE - KEPT HIDDEN FOR REFERENCE
    // if (!user?.id) return;
    // const oldParams = {
    //   root_user_id: user.id,
    //   depth: 8,
    //   include_line_manager: true,
    //   ...(companyId ? { company: companyId } : {}),
    // };
    // getOrgChartApi(oldParams)
    //   .then((res) => {
    //     const chart = res.data?.results?.[0] || null;
    //     setOrgChartData(chart);
    //     const managerId = chart?.employment?.report_to?.id;
    //     setExpandedIds(new Set(managerId ? [managerId, user.id] : [user.id]));
    //   })
    //   .catch((err) => {
    //     console.error("Error fetching org chart", err);
    //     setOrgChartData(null);
    //     setError("Unable to load org chart");
    //   })
    //   .finally(() => setOrgChartLoading(false));
  }, [user, hasOrgChartAccess]);

  useEffect(() => {
    if (!zoomContainerRef.current || !rootNodeRef.current) return;
    if (orgDirection !== "horizontal") return;
    const container = zoomContainerRef.current;
    const root = rootNodeRef.current;
    // Calculate scroll position to center the root node
    const containerRect = container.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const scrollLeft = rootRect.left - containerRect.left + container.scrollLeft - (containerRect.width / 2) + (rootRect.width / 2);
    container.scrollTo({ left: Math.max(scrollLeft, 0), behavior: "smooth" });
  }, [orgChartData, orgChartRoots, orgDirection, zoomLevel, expandedIds]);

  if (!hasOrgChartAccess) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
          <div className="card shadow-sm" style={{ maxWidth: 520, width: "100%", border: "none", borderRadius: "14px" }}>
            <div className="card-body text-center">
              <i className="las la-ban" style={{ fontSize: "36px", color: "#d3001a" }} />
              <h5 style={{ marginTop: 10 }}>You don&apos;t have access to Org Chart.</h5>
              <p style={{ color: "#6b7280" }}>Please contact your administrator.</p>
              <div className="d-flex justify-content-center" style={{ gap: 10 }}>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                  Go Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate("/employees")}
                  style={{ backgroundColor: "rgb(211, 0, 26)", borderColor: "rgb(211, 0, 26)" }}
                >
                  Employee Listing
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "10px", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: 0 }}>Organization Chart</h3>
            <div style={{ color: "#6b7280", fontSize: "14px" }}>View reporting relationships across the organization.</div>
          </div>
          <div className="org-controls" style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setOrgDirection("horizontal")}
              style={{
                padding: isMobile ? "6px" : "6px 10px",
                borderRadius: "8px",
                border: orgDirection === "horizontal" ? "1px solid #d3001a" : "1px solid #e5e7eb",
                background: orgDirection === "horizontal" ? "#fff1f2" : "#ffffff",
                color: orgDirection === "horizontal" ? "#b91c1c" : "#374151",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "4px",
                minWidth: isMobile ? "32px" : "auto",
                justifyContent: "center",
              }}
              title="Horizontal"
            >
              <i className="las la-arrows-alt-h" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
              {!isMobile && <span>Horizontal</span>}
            </button>
            <button
              type="button"
              onClick={() => setOrgDirection("vertical")}
              style={{
                padding: isMobile ? "6px" : "6px 10px",
                borderRadius: "8px",
                border: orgDirection === "vertical" ? "1px solid #d3001a" : "1px solid #e5e7eb",
                background: orgDirection === "vertical" ? "#fff1f2" : "#ffffff",
                color: orgDirection === "vertical" ? "#b91c1c" : "#374151",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "4px",
                minWidth: isMobile ? "32px" : "auto",
                justifyContent: "center",
              }}
              title="Vertical"
            >
              <i className="las la-arrows-alt-v" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
              {!isMobile && <span>Vertical</span>}
            </button>
            <div style={{ width: "1px", height: "20px", background: "#e5e7eb", margin: "0 4px" }}></div>
            <button
              type="button"
              onClick={expandAll}
              style={{
                padding: isMobile ? "6px" : "6px 10px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: "#ffffff",
                color: "#374151",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "4px",
                minWidth: isMobile ? "32px" : "auto",
                justifyContent: "center",
              }}
              title="Expand All"
            >
              <i className="las la-angle-double-down" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
              {!isMobile && <span style={{ marginLeft: "4px" }}>Expand All</span>}
            </button>
            <button
              type="button"
              onClick={collapseAll}
              style={{
                padding: isMobile ? "6px" : "6px 10px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: "#ffffff",
                color: "#374151",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "4px",
                minWidth: isMobile ? "32px" : "auto",
                justifyContent: "center",
              }}
              title="Collapse All"
            >
              <i className="las la-angle-double-up" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
              {!isMobile && <span style={{ marginLeft: "4px" }}>Collapse All</span>}
            </button>
            <div style={{ width: "1px", height: "20px", background: "#e5e7eb", margin: "0 4px" }}></div>
            <button
              type="button"
              onClick={() => setZoomLevel((prev) => Math.min(prev + 0.1, 2))}
              disabled={zoomLevel >= 2}
              style={{
                padding: isMobile ? "6px" : "6px 10px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: zoomLevel >= 2 ? "#f3f4f6" : "#ffffff",
                color: zoomLevel >= 2 ? "#9ca3af" : "#374151",
                fontSize: "12px",
                fontWeight: 600,
                cursor: zoomLevel >= 2 ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "4px",
                minWidth: isMobile ? "32px" : "auto",
                justifyContent: "center",
              }}
              title="Zoom In"
            >
              <i className="las la-search-plus" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
              {!isMobile && <span style={{ marginLeft: "4px" }}>Zoom In</span>}
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5))}
              disabled={zoomLevel <= 0.5}
              style={{
                padding: isMobile ? "6px" : "6px 10px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: zoomLevel <= 0.5 ? "#f3f4f6" : "#ffffff",
                color: zoomLevel <= 0.5 ? "#9ca3af" : "#374151",
                fontSize: "12px",
                fontWeight: 600,
                cursor: zoomLevel <= 0.5 ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "4px",
                minWidth: isMobile ? "32px" : "auto",
                justifyContent: "center",
              }}
              title="Zoom Out"
            >
              <i className="las la-search-minus" style={{ fontSize: isMobile ? "16px" : "14px" }}></i>
              {!isMobile && <span style={{ marginLeft: "4px" }}>Zoom Out</span>}
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel(1)}
              disabled={zoomLevel === 1}
              style={{
                padding: isMobile ? "6px" : "6px 10px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: zoomLevel === 1 ? "#f3f4f6" : "#ffffff",
                color: zoomLevel === 1 ? "#9ca3af" : "#374151",
                fontSize: "12px",
                fontWeight: 600,
                cursor: zoomLevel === 1 ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "4px",
                minWidth: isMobile ? "32px" : "auto",
                justifyContent: "center",
                overflow: "visible",
              }}
              title="Reset Zoom"
            >
              <i className="las la-compress" style={{ fontSize: isMobile ? "16px" : "14px", display: "block" }}></i>
              {!isMobile && <span style={{ marginLeft: "4px" }}>Reset</span>}
            </button>
            {!isMobile && (
              <span style={{ fontSize: "12px", color: "#6b7280", marginLeft: "4px" }}>
                {Math.round(zoomLevel * 100)}%
              </span>
            )}
          </div>
        </div>

        <div className="card shadow-sm" style={{ border: "1px solid #e5e7eb", borderRadius: "12px" }}>
          <div className="card-body" style={{ padding: "12px" }}>
            <style>{`
              .org-node {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 8px;
                min-width: 220px;
              }
              .org-card {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                background: #ffffff;
                border: 1px solid #e5e7eb;
                border-radius: 10px;
                padding: 10px 14px 8px 14px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                width: 260px;
                height: 100px;
                position: relative;
                overflow: hidden;
              }
              .org-avatar {
                width: 48px;
                height: 48px;
                border-radius: 50%;
                overflow: hidden;
                background: #f3f4f6;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                color: #6b7280;
                flex-shrink: 0;
              }
              .org-placeholder {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #fee2e2;
                color: #b91c1c;
                font-weight: 700;
              }
              .org-meta {
                display: flex;
                flex-direction: column;
                gap: 2px;
                flex: 1;
                justify-content: flex-start;
                min-width: 0;
                overflow: hidden;
              }
              .org-name {
                font-weight: 600;
                color: #111827;
                font-size: 14px;
                white-space: nowrap;
                overflow-x: auto;
                overflow-y: hidden;
                width: 100%;
                min-width: 0;
                scrollbar-width: thin;
                scrollbar-color: rgba(0,0,0,0.2) transparent;
              }
              .org-name::-webkit-scrollbar {
                height: 3px;
              }
              .org-name::-webkit-scrollbar-track {
                background: transparent;
              }
              .org-name::-webkit-scrollbar-thumb {
                background: rgba(0,0,0,0.2);
                border-radius: 3px;
              }
              .org-code {
                font-size: 12px;
                color: #1f2937;
                font-weight: 600;
                white-space: nowrap;
              }
              .org-toggle {
                position: absolute;
                bottom: 8px;
                right: 8px;
                border: 1px solid #e5e7eb;
                background: #f9fafb;
                border-radius: 10px;
                width: 32px;
                height: 32px;
                font-size: 14px;
                color: #1f2937;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.15s ease;
              }
              .org-toggle:hover {
                background: #eef2ff;
                border-color: #c7d2fe;
              }
              .org-count {
                position: absolute;
                top: -6px;
                right: -6px;
                background: #ef4444;
                color: #ffffff;
                border-radius: 999px;
                font-size: 10px;
                padding: 2px 5px;
                line-height: 1;
                min-width: 18px;
                text-align: center;
              }
              .org-title {
                font-size: 12px;
                color: #6b7280;
                white-space: nowrap;
                overflow-x: auto;
                overflow-y: hidden;
                width: 100%;
                min-width: 0;
                scrollbar-width: thin;
                scrollbar-color: rgba(0,0,0,0.2) transparent;
              }
              .org-title::-webkit-scrollbar {
                height: 3px;
              }
              .org-title::-webkit-scrollbar-track {
                background: transparent;
              }
              .org-title::-webkit-scrollbar-thumb {
                background: rgba(0,0,0,0.2);
                border-radius: 3px;
              }
              .org-dept {
                font-size: 12px;
                color: #9ca3af;
                white-space: nowrap;
                overflow-x: auto;
                overflow-y: hidden;
                width: 100%;
                min-width: 0;
                scrollbar-width: thin;
                scrollbar-color: rgba(0,0,0,0.2) transparent;
              }
              .org-dept::-webkit-scrollbar {
                height: 3px;
              }
              .org-dept::-webkit-scrollbar-track {
                background: transparent;
              }
              .org-dept::-webkit-scrollbar-thumb {
                background: rgba(0,0,0,0.2);
                border-radius: 3px;
              }
              .org-children {
                display: flex;
                flex-wrap: nowrap;
                gap: 16px;
                margin-top: 16px;
                padding: 24px 24px 8px 24px;
                justify-content: center;
                position: relative;
                overflow-x: auto;
                overflow-y: visible;
              }
              .org-children::before {
                content: "";
                position: absolute;
                top: 0;
                left: 50%;
                width: 1px;
                height: 16px;
                background: #d1d5db;
                transform: translateX(-50%);
              }
              .org-children::after {
                content: "";
                position: absolute;
                top: 16px;
                left: 16px;
                right: 16px;
                border-top: 1px solid #d1d5db;
              }
              .org-children > .org-node {
                position: relative;
                padding-top: 16px;
              }
              .org-children > .org-node::before {
                content: "";
                position: absolute;
                top: 0;
                left: 50%;
                width: 1px;
                height: 16px;
                background: #d1d5db;
                transform: translateX(-50%);
              }
              .org-children.vertical {
                flex-direction: column;
                align-items: center;
                gap: 12px;
                padding: 12px 0 0 0;
                margin-top: 12px;
                overflow-x: visible;
              }
              .org-children.vertical::before,
              .org-children.vertical::after,
              .org-children.vertical > .org-node::before {
                display: none;
              }
            `}</style>
            <div
              ref={zoomContainerRef}
              style={{
                overflow: "auto",
                minHeight: "200px",
                position: "relative",
                width: "100%",
                height: "600px",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div
                ref={chartContainerRef}
                style={{
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: "top center",
                  transition: "transform 0.2s ease",
                  padding: "20px",
                  minWidth: "max-content",
                  width: "max-content",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  margin: "0 auto",
                }}
              >
              {orgChartLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "180px", width: "100%" }}>
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              ) : error ? (
                <div className="text-center" style={{ padding: "32px 16px", color: "#9ca3af", width: "100%" }}>
                  <i className="las la-sitemap" style={{ fontSize: "36px", marginBottom: "8px", display: "block" }}></i>
                  <div style={{ fontSize: "14px" }}>{error}</div>
                </div>
              ) : orgChartRoots.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "max-content",
                    padding: "0 8px 8px 0",
                    flexDirection: "row",
                    gap: "24px",
                    flexWrap: "wrap",
                  }}
                >
                  {orgChartRoots.map((rootNode, index) => (
                    <div
                      key={rootNode.id}
                      ref={index === 0 ? rootNodeRef : null}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        width: "max-content",
                      }}
                    >
                      <OrgChartNode node={rootNode} direction={orgDirection} />
                    </div>
                  ))}
                </div>
              ) : orgChartData ? (
                // OLD ENDPOINT RENDERING - KEPT HIDDEN FOR REFERENCE
                <div
                  ref={rootNodeRef}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "max-content",
                    padding: "0 8px 8px 0",
                  }}
                >
                  <OrgChartNode node={orgChartData} direction={orgDirection} />
                </div>
              ) : (
                <div className="text-center" style={{ padding: "32px 16px", color: "#9ca3af", width: "100%" }}>
                  <i className="las la-sitemap" style={{ fontSize: "36px", marginBottom: "8px", display: "block" }}></i>
                  <div style={{ fontSize: "14px" }}>No org chart data available</div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={profileModalOpen}
        onCancel={() => {
          setProfileModalOpen(false);
          setSelectedProfile(null);
        }}
        footer={null}
        centered
        width={400}
        maskClosable={true}
      >
        {selectedProfile && (
          <div style={{ padding: "20px", textAlign: "center", position: "relative" }}>
            <button
              onClick={() => {
                setProfileModalOpen(false);
                setSelectedProfile(null);
              }}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "transparent",
                border: "none",
                fontSize: "20px",
                color: "#6b7280",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f3f4f6";
                e.target.style.color = "#111827";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#6b7280";
              }}
              aria-label="Close"
            >
              <i className="las la-times" />
            </button>
            <div style={{ marginBottom: "20px" }}>
              {selectedProfile.profile_image ? (
                <Image
                  src={selectedProfile.profile_image}
                  type="user"
                  alt={selectedProfile.name}
                  style={{ 
                    width: "180px", 
                    height: "180px", 
                    borderRadius: "50%", 
                    objectFit: "cover",
                    margin: "0 auto",
                    display: "block"
                  }}
                />
              ) : (
                <div 
                  style={{ 
                    width: "180px", 
                    height: "180px", 
                    borderRadius: "50%", 
                    background: "#fee2e2",
                    color: "#b91c1c",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "54px",
                    fontWeight: 700,
                    margin: "0 auto"
                  }}
                >
                  {getInitials(selectedProfile.name)}
                </div>
              )}
            </div>
            <div style={{ marginBottom: "8px" }}>
              <h4 style={{ margin: 0, fontSize: "20px", fontWeight: 600, color: "#111827" }}>
                {selectedProfile.name}
              </h4>
            </div>
            {selectedProfile.title && (
              <div style={{ marginBottom: "8px", fontSize: "14px", color: "#6b7280" }}>
                <strong>Position:</strong> {selectedProfile.title}
              </div>
            )}
            {selectedProfile.department && (
              <div style={{ marginBottom: "8px", fontSize: "14px", color: "#6b7280" }}>
                <strong>Department:</strong> {selectedProfile.department}
              </div>
            )}
            {allowedEmployeeIds.includes(selectedProfile.id) && (
              <div style={{ marginTop: "20px" }}>
                <Link
                  to={`${basePath}/${selectedProfile.id}`}
                  className="btn btn-primary"
                  style={{ 
                    backgroundColor: "rgb(211, 0, 26)", 
                    borderColor: "rgb(211, 0, 26)",
                    textDecoration: "none",
                    display: "inline-block",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    color: "#fff"
                  }}
                >
                  View Profile
                </Link>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrgChartPage;

